import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { adminCmsAPI } from '../../lib/cmsApi';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  FileText, Pencil, ArrowLeft, Loader2, Save, Eye, EyeOff,
  ChevronDown, ChevronUp, GripVertical, X,
} from 'lucide-react';
import ImagePicker from '../../components/cms/ImagePicker';

const CmsPages = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionForm, setSectionForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.role !== 'administrateur' && user?.role !== 'employe') {
      navigate('/portail');
    }
  }, [user, navigate]);

  const fetchPages = async () => {
    try {
      const res = await adminCmsAPI.listPages();
      setPages(res.data || []);
    } catch (e) { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPages(); }, []);

  const openPageEdit = async (slug) => {
    try {
      const res = await adminCmsAPI.getPage(slug);
      setEditingPage(res.data);
      setEditingSection(null);
    } catch (e) { toast.error('Erreur'); }
  };

  const openSectionEdit = (section) => {
    setEditingSection(section.section_key || section.id);
    setSectionForm({
      title: section.title || '',
      eyebrow: section.eyebrow || '',
      subtitle: section.subtitle || '',
      content: section.content || '',
      image_url: section.image_url || '',
      cta_text: section.cta_text || '',
      cta_link: section.cta_link || '',
      is_visible: section.is_visible !== false,
      items: section.items || [],
    });
  };

  const saveSection = async () => {
    if (!editingPage || !editingSection) return;
    setSaving(true);
    try {
      await adminCmsAPI.updatePageSection(editingPage.slug, editingSection, sectionForm);
      toast.success('Section mise a jour');
      // Refresh page data
      const res = await adminCmsAPI.getPage(editingPage.slug);
      setEditingPage(res.data);
      setEditingSection(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally { setSaving(false); }
  };

  const sectionTypeLabels = {
    hero: 'Section hero',
    image_text: 'Image et texte',
    features: 'Liste de fonctionnalites',
    values: 'Valeurs',
    stats: 'Statistiques',
    cta: 'Appel a l\'action',
    text: 'Texte',
    cards: 'Cartes',
    testimonials: 'Temoignages',
    faq: 'FAQ',
    gallery: 'Galerie',
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" />
      </div>
    );
  }

  // Main page list view
  if (!editingPage) {
    return (
      <div className="min-h-screen bg-neutral-50 p-6 lg:p-8" data-testid="cms-pages">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="font-heading text-2xl font-bold text-[#1D1D1B]">Pages du site</h1>
              <p className="text-neutral-500 text-sm">{pages.length} page(s) - Cliquez sur une page pour editer ses sections</p>
            </div>
            <Button variant="outline" onClick={() => navigate('/cms')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Retour au CMS
            </Button>
          </div>
          <div className="space-y-3">
            {pages.map(page => (
              <Card
                key={page.slug}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => openPageEdit(page.slug)}
                data-testid={`page-card-${page.slug}`}
              >
                <CardContent className="p-5 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.15) 0%, rgba(249,180,56,0.15) 100%)' }}>
                      <FileText className="w-6 h-6 text-[#F9B438]" />
                    </div>
                    <div>
                      <h3 className="font-medium text-[#1D1D1B] text-lg">{page.title}</h3>
                      <div className="flex items-center gap-3 text-sm text-neutral-500">
                        <span>/{page.slug}</span>
                        <span>{page.sections?.length || 0} section(s)</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={page.status === 'publie' ? 'default' : 'secondary'}>
                      {page.status === 'publie' ? 'Publiee' : 'Brouillon'}
                    </Badge>
                    <Button variant="ghost" size="sm">
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Page detail / section editor
  return (
    <div className="min-h-screen bg-neutral-50 p-6 lg:p-8" data-testid="cms-page-edit">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <button
              onClick={() => { setEditingPage(null); setEditingSection(null); }}
              className="flex items-center gap-1 text-sm text-neutral-500 hover:text-[#F9B438] mb-2 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Retour aux pages
            </button>
            <h1 className="font-heading text-2xl font-bold text-[#1D1D1B]">{editingPage.title}</h1>
            <p className="text-neutral-500 text-sm">Slug: /{editingPage.slug} - {editingPage.sections?.length || 0} section(s)</p>
          </div>
          <div className="flex gap-2">
            <Badge variant={editingPage.status === 'publie' ? 'default' : 'secondary'}>
              {editingPage.status === 'publie' ? 'Publiee' : 'Brouillon'}
            </Badge>
          </div>
        </div>

        {/* SEO Info */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-neutral-500">Meta titre:</span>
                <p className="text-[#1D1D1B] font-medium">{editingPage.meta_title || '-'}</p>
              </div>
              <div>
                <span className="text-neutral-500">Meta description:</span>
                <p className="text-[#1D1D1B]">{editingPage.meta_description || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sections */}
        <div className="space-y-3">
          {(editingPage.sections || []).map((section, index) => (
            <Card
              key={section.section_key || section.id || index}
              className={`transition-all ${editingSection === (section.section_key || section.id) ? 'ring-2 ring-[#F9B438]' : 'hover:shadow-md'}`}
            >
              <CardContent className="p-0">
                {/* Section header */}
                <div
                  className="p-4 flex items-center justify-between cursor-pointer"
                  onClick={() => {
                    if (editingSection === (section.section_key || section.id)) {
                      setEditingSection(null);
                    } else {
                      openSectionEdit(section);
                    }
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-neutral-300" />
                      <span className="text-xs font-mono text-neutral-400 bg-neutral-100 px-2 py-0.5 rounded">
                        {section.section_type}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-medium text-[#1D1D1B]">
                        {section.title || sectionTypeLabels[section.section_type] || 'Section'}
                      </h3>
                      {section.eyebrow && <p className="text-xs text-[#F9B438]">{section.eyebrow}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!section.is_visible && (
                      <Badge variant="outline" className="text-xs"><EyeOff className="w-3 h-3 mr-1" />Masquee</Badge>
                    )}
                    <Button variant="ghost" size="sm">
                      {editingSection === (section.section_key || section.id) ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                {/* Section edit form */}
                {editingSection === (section.section_key || section.id) && (
                  <div className="border-t p-4 space-y-4 bg-neutral-50/50">
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Titre</Label><Input value={sectionForm.title} onChange={e => setSectionForm({...sectionForm, title: e.target.value})} /></div>
                      <div><Label>Surtitre (eyebrow)</Label><Input value={sectionForm.eyebrow} onChange={e => setSectionForm({...sectionForm, eyebrow: e.target.value})} /></div>
                    </div>
                    <div><Label>Sous-titre</Label><Input value={sectionForm.subtitle} onChange={e => setSectionForm({...sectionForm, subtitle: e.target.value})} /></div>
                    <div><Label>Contenu</Label><Textarea value={sectionForm.content} onChange={e => setSectionForm({...sectionForm, content: e.target.value})} rows={6} /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <ImagePicker value={sectionForm.image_url} onChange={url => setSectionForm({...sectionForm, image_url: url})} folder="pages" label="Image" />
                      <div className="flex items-end gap-4">
                        <div className="flex-1"><Label>Texte CTA</Label><Input value={sectionForm.cta_text} onChange={e => setSectionForm({...sectionForm, cta_text: e.target.value})} /></div>
                        <div className="flex-1"><Label>Lien CTA</Label><Input value={sectionForm.cta_link} onChange={e => setSectionForm({...sectionForm, cta_link: e.target.value})} /></div>
                      </div>
                    </div>

                    {/* Items editor */}
                    {sectionForm.items?.length > 0 && (
                      <div>
                        <Label className="mb-2 block">Elements ({sectionForm.items.length})</Label>
                        <div className="space-y-2">
                          {sectionForm.items.map((item, idx) => (
                            <Card key={idx} className="bg-white">
                              <CardContent className="p-3 space-y-2">
                                <div className="grid grid-cols-2 gap-2">
                                  <Input
                                    value={item.title || ''}
                                    onChange={e => {
                                      const items = [...sectionForm.items];
                                      items[idx] = {...items[idx], title: e.target.value};
                                      setSectionForm({...sectionForm, items});
                                    }}
                                    placeholder="Titre"
                                    className="text-sm"
                                  />
                                  <Input
                                    value={item.icon || ''}
                                    onChange={e => {
                                      const items = [...sectionForm.items];
                                      items[idx] = {...items[idx], icon: e.target.value};
                                      setSectionForm({...sectionForm, items});
                                    }}
                                    placeholder="Icone"
                                    className="text-sm"
                                  />
                                </div>
                                <Textarea
                                  value={item.description || item.content || ''}
                                  onChange={e => {
                                    const items = [...sectionForm.items];
                                    items[idx] = {...items[idx], description: e.target.value, content: e.target.value};
                                    setSectionForm({...sectionForm, items});
                                  }}
                                  placeholder="Description"
                                  rows={2}
                                  className="text-sm"
                                />
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={sectionForm.is_visible} onChange={e => setSectionForm({...sectionForm, is_visible: e.target.checked})} />
                        <span className="text-sm flex items-center gap-1"><Eye className="w-4 h-4" /> Visible</span>
                      </label>
                    </div>

                    <div className="flex gap-3 pt-2">
                      <Button variant="outline" onClick={() => setEditingSection(null)} className="flex-1">Annuler</Button>
                      <Button onClick={saveSection} disabled={saving} className="flex-1" data-testid="save-section-btn">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                        Enregistrer
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CmsPages;
